import { Link } from "@tanstack/react-router";
import { ArrowLeft, Truck, MapPin, Clock, Package } from "lucide-react";
import { useShopSettings, DEFAULT_SHOP_SETTINGS } from "@/hooks/useShopSettings";

export default function Shipping() {
  const { data: settings } = useShopSettings();
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-4" />
            Nazad
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10 sm:py-16">
        {/* Title */}
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-2">
          Dostava i lično preuzimanje
        </h1>
        <p className="text-muted-foreground text-sm mb-10 sm:mb-14">
          Sve informacije o načinima preuzimanja vaše narudžbe.
        </p>

        <div className="space-y-12 sm:space-y-16">
          {/* Section 1 — Brza pošta */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <div className="size-10 rounded-xl bg-foreground flex items-center justify-center">
                <Truck className="size-5 text-background" />
              </div>
              <h2 className="text-lg font-semibold">Slanje brzom poštom</h2>
            </div>

            <div className="text-[15px] leading-relaxed text-foreground/80 space-y-4">
              <p>
                Dostavu paketa vršimo putem <strong>X-Express</strong> brze pošte.
                Sve narudžbe zaprimljene prije 14:00h radnim danima, šaljemo isti dan.
                Tokom vikenda brza pošta ne radi, te pakete zaprimljene tokom vikenda šaljemo u ponedjeljak.
              </p>
              <p>
                Rok dostave je od 1 do 3 radna dana, zavisno od Vaše lokacije.
              </p>
            </div>

            {/* Key facts */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
              <div className="bg-white rounded-xl border border-border/60 px-4 py-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-1.5">Cijena dostave</p>
                <p className="text-xl font-semibold tabular-nums">11 <span className="text-sm font-normal text-muted-foreground">KM</span></p>
              </div>
              <div className="bg-white rounded-xl border border-border/60 px-4 py-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-1.5">Rok dostave</p>
                <p className="text-xl font-semibold tabular-nums">1–3 <span className="text-sm font-normal text-muted-foreground">radna dana</span></p>
              </div>
              <div className="bg-white rounded-xl border border-border/60 px-4 py-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-1.5">Cutoff vrijeme</p>
                <div className="flex items-center gap-1.5">
                  <Clock className="size-4 text-muted-foreground" />
                  <p className="text-xl font-semibold tabular-nums">14:00<span className="text-sm font-normal text-muted-foreground ml-0.5">h</span></p>
                </div>
              </div>
            </div>
          </section>

          {/* Divider */}
          <div className="h-px bg-border/60" />

          {/* Section 2 — Lično preuzimanje */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <div className="size-10 rounded-xl bg-foreground flex items-center justify-center">
                <MapPin className="size-5 text-background" />
              </div>
              <h2 className="text-lg font-semibold">Lično preuzimanje</h2>
            </div>

            <div className="text-[15px] leading-relaxed text-foreground/80 space-y-4">
              <p>
                Lično preuzimanje je dostupno isključivo na područiju <strong>Općine Tešanj</strong>.
                Za detaljnije informacije gdje možete preuzeti narudžbu javite se korisničkoj službi na Facebook ili Instagram.
              </p>
              <p>
                Lično možete doći preuzeti 24h nakon Vaše narudžbe, rok za preuzimanje je 7 dana od dana narudžbe.
              </p>
            </div>

            {/* Key facts */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
              <div className="bg-white rounded-xl border border-border/60 px-4 py-4 flex items-center gap-4">
                <div className="size-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                  <Package className="size-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-0.5">Dostupno za preuzimanje</p>
                  <p className="text-base font-semibold">nakon 24h</p>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-border/60 px-4 py-4 flex items-center gap-4">
                <div className="size-10 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                  <Clock className="size-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-0.5">Rok za preuzimanje</p>
                  <p className="text-base font-semibold">7 dana</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/50 mt-10">
        <div className="max-w-3xl mx-auto px-4 py-8 text-center text-xs text-muted-foreground tracking-wide">
          {settings?.shop_name ?? DEFAULT_SHOP_SETTINGS.shop_name} · Bosna i Hercegovina
        </div>
      </footer>
    </div>
  );
}
